import React, { useState, useEffect } from 'react';
import { supabase } from '../api/supabase';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  IconButton, 
  Badge, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  DialogActions, 
  Autocomplete, 
  TextField, 
  Alert, 
  Snackbar,
  Card,
  CardActionArea,
  CardMedia,
  CardContent
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CakeIcon from '@mui/icons-material/Cake';
import Loader from '../components/common/Loader';

// Componente para cada produto na grade
const ProductCard = ({ product, onAddToCart }) => {
  const imageUrl = product.image_url ? `${process.env.PUBLIC_URL}/images/${product.image_url}` : '';
  const isOutOfStock = product.quantity <= 0;

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        opacity: isOutOfStock ? 0.6 : 1,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: isOutOfStock ? 'none' : 'translateY(-4px)',
          boxShadow: isOutOfStock ? 'none' : '0 8px 24px 0 rgba(240, 98, 146, 0.2)',
        }
      }}
    >
      <CardActionArea 
        onClick={() => !isOutOfStock && onAddToCart(product)} 
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }} 
        disabled={isOutOfStock}
      >
        <CardMedia
          component="div"
          sx={{
            height: 160, 
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: !imageUrl ? '#FCE4EC' : 'transparent', 
            backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
            backgroundSize: 'contain', 
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {!imageUrl && <CakeIcon sx={{ fontSize: 60, color: 'primary.light' }} />}
        </CardMedia>
        <CardContent sx={{ textAlign: 'center', pt: 1, flexGrow: 1 }}>
          <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            {product.name}
          </Typography>
          <Typography variant="h5" color="primary.dark" sx={{ fontWeight: 'bold' }}>
            {Number(product.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </Typography>
          {isOutOfStock && (
            <Typography variant="button" color="error" sx={{ fontWeight: 'bold', mt: 1, display: 'block' }}>
              ESGOTADO
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const POSPage = () => {
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [cart, setCart] = useState([]);
  const [openCart, setOpenCart] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('Dinheiro');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    const { data: productsData } = await supabase.from('products').select('*').order('name');
    const { data: clientsData } = await supabase.from('clients').select('*').order('name');
    setProducts(productsData || []);
    setClients(clientsData || []);
    setLoading(false);
  };

  const handleAddToCart = (product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        if (existingItem.quantity >= product.quantity) {
          setNotification({ open: true, message: `Estoque máximo para ${product.name} atingido!`, severity: 'warning'});
          return prevCart;
        }
        return prevCart.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
  };
  
  const handleRemoveFromCart = (productId) => {
    setCart(prevCart => {
        const itemToRemove = prevCart.find(item => item.id === productId);
        if (itemToRemove.quantity > 1) {
            return prevCart.map(item => item.id === productId ? {...item, quantity: item.quantity - 1} : item);
        }
        return prevCart.filter(item => item.id !== productId)
    });
  };

  const cartTotal = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  const cartItemCount = cart.reduce((acc, item) => acc + item.quantity, 0);
  
  const handleFinalizeSale = async () => {
    if (cart.length === 0) return;

    const cartItemsForDB = cart.map(item => ({ id: item.id, quantity: item.quantity }));
    const params = {
        client_id_param: selectedClient ? selectedClient.id : null,
        payment_method_param: paymentMethod,
        cart_items: cartItemsForDB
    };

    const { data, error } = await supabase.rpc('create_sale_and_update_stock', params);

    if (error || (data && data.error)) {
        const errorMessage = (data && data.error) ? data.error : (error ? error.message : 'Ocorreu um erro desconhecido.');
        setNotification({ open: true, message: `Falha na venda: ${errorMessage}`, severity: 'error' });
    } else {
        const successMessage = data.message || 'Venda finalizada com sucesso!';
        setNotification({ open: true, message: successMessage, severity: 'success' });
        setCart([]);
        setSelectedClient(null);
        setOpenCart(false);
        fetchInitialData();
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Ponto de Venda</Typography>
        <IconButton color="primary" onClick={() => setOpenCart(true)}>
          <Badge badgeContent={cartItemCount} color="secondary"><ShoppingCartIcon /></Badge>
        </IconButton>
      </Box>
      {loading ? <Loader /> : (
        <Grid container spacing={2}>
          {products.length > 0 ? products.map(p => (
            // CORREÇÃO: Removida a propriedade "item"
            <Grid xs={6} sm={4} md={4} lg={3} key={p.id}>
                <ProductCard product={p} onAddToCart={handleAddToCart} />
            </Grid>
          )) : (
            // CORREÇÃO: Removida a propriedade "item"
            <Grid xs={12}>
                <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                    Nenhum produto cadastrado. Adicione produtos na página "Produtos" para começar.
                </Typography>
            </Grid>
          )}
        </Grid>
      )}
      
      <Dialog open={openCart} onClose={() => setOpenCart(false)} fullWidth maxWidth="sm">
        <DialogTitle>Finalizar Venda</DialogTitle>
        <DialogContent>
          {cart.length > 0 ? cart.map(item => (
            <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, borderBottom: '1px solid #eee', pb: 1 }}>
              <Box>
                <Typography fontWeight="bold">{item.quantity}x {item.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {item.quantity} x {Number(item.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontWeight="bold">{(item.quantity * item.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Typography>
                <IconButton size="small" onClick={() => handleRemoveFromCart(item.id)} sx={{ ml: 1 }}><RemoveCircleOutlineIcon color="error" /></IconButton>
              </Box>
            </Box>
          )) : <Typography>Carrinho vazio. Adicione produtos para continuar.</Typography>}
          
          {cart.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 2, textAlign: 'right', fontWeight: 'bold' }}>
                Total: {cartTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </Typography>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => `${option.name} - ${option.phone}`}
                onChange={(event, newValue) => setSelectedClient(newValue)}
                renderInput={(params) => <TextField {...params} label="Cliente VIP (opcional)" margin="normal" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Forma de Pagamento</InputLabel>
                <Select value={paymentMethod} label="Forma de Pagamento" onChange={(e) => setPaymentMethod(e.target.value)}>
                  <MenuItem value="Dinheiro">Dinheiro</MenuItem>
                  <MenuItem value="Cartão de Crédito">Cartão de Crédito</MenuItem>
                  <MenuItem value="Cartão de Débito">Cartão de Débito</MenuItem>
                  <MenuItem value="Pix">Pix</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCart(false)}>Cancelar</Button>
          <Button onClick={handleFinalizeSale} variant="contained" disabled={cart.length === 0}>Finalizar Venda</Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar open={notification.open} autoHideDuration={6000} onClose={() => setNotification(prev => ({...prev, open: false}))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setNotification(prev => ({...prev, open: false}))} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default POSPage;